import React from "react";
import styled from "styled-components";
import { AlertTriangle, CheckCircle, Info, X } from "lucide-react";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  width: 100%;
  max-width: 440px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(245, 158, 11, 0.1);
  color: #fff;
  animation: modalFadeIn 0.2s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const IconWrapper = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) => (props.$danger ? "rgba(239, 68, 68, 0.15)" : "rgba(245, 158, 11, 0.15)")};
  color: ${(props) => (props.$danger ? "#ef4444" : "#f59e0b")};
`;

const ModalTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.35rem 0;
`;

const ModalMessage = styled.p`
  font-size: 0.9rem;
  color: #9ca3af;
  margin: 0;
  line-height: 1.45;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &.cancel {
    background: #1f2937;
    color: #e5e7eb;
    border-color: #374151;
    &:hover {
      background: #374151;
    }
  }

  &.danger {
    background: #dc2626;
    color: #ffffff;
    &:hover {
      background: #b91c1c;
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
    }
  }

  &.primary {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #0b0f19;
    font-weight: 600;
    &:hover {
      background: linear-gradient(135deg, #fbbf24, #b45309);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
    }
  }
`;

export const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, isDanger = true, confirmText = "Confirm" }) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onCancel}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <IconWrapper $danger={isDanger}>
            {isDanger ? <AlertTriangle size={22} /> : <Info size={22} />}
          </IconWrapper>
          <div>
            <ModalTitle>{title || "Are you sure?"}</ModalTitle>
            <ModalMessage>{message}</ModalMessage>
          </div>
        </ModalHeader>
        <ModalActions>
          <ModalButton className="cancel" onClick={onCancel}>
            Cancel
          </ModalButton>
          <ModalButton className={isDanger ? "danger" : "primary"} onClick={onConfirm}>
            {confirmText}
          </ModalButton>
        </ModalActions>
      </ModalBox>
    </Overlay>
  );
};

// Toast notification container & banner
const ToastWrapper = styled.div`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  pointer-events: none;

  @media (max-width: 480px) {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
  }
`;

const ToastItem = styled.div`
  pointer-events: auto;
  background: #111827;
  border-left: 4px solid ${(props) => (props.$type === "error" ? "#ef4444" : props.$type === "info" ? "#3b82f6" : "#10b981")};
  border-top: 1px solid #1f293d;
  border-right: 1px solid #1f293d;
  border-bottom: 1px solid #1f293d;
  border-radius: 8px;
  padding: 0.85rem 1.1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  animation: slideInRight 0.25s ease-out;
  min-width: 280px;
  max-width: 400px;

  @media (max-width: 480px) {
    min-width: auto;
    max-width: 100%;
    width: 100%;
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

export const ToastNotification = ({ toast, onClose }) => {
  if (!toast) return null;

  return (
    <ToastWrapper>
      <ToastItem $type={toast.type}>
        {toast.type === "error" ? (
          <AlertTriangle size={18} color="#ef4444" />
        ) : toast.type === "info" ? (
          <Info size={18} color="#3b82f6" />
        ) : (
          <CheckCircle size={18} color="#10b981" />
        )}
        <span style={{ fontSize: "0.88rem", flex: 1, color: "#f3f4f6" }}>{toast.message}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            padding: "2px",
            display: "flex",
          }}
        >
          <X size={15} />
        </button>
      </ToastItem>
    </ToastWrapper>
  );
};
